import { defineComponent, ref, computed, createElementBlock, openBlock, createCommentVNode, createBlock, Fragment, unref, withCtx, renderSlot, createVNode, normalizeClass, createTextVNode, toDisplayString, createElementVNode } from "vue";
import { CalendarDate, Time, parseDate } from "@internationalized/date";
import { useI18n } from "vue-i18n";
import { I18n } from "app-core";
import moment from "moment-timezone";
import UPopover from "@nuxt/ui/components/Popover.vue";
import UButton from "@nuxt/ui/components/Button.vue";
import UCalendar from "@nuxt/ui/components/Calendar.vue";
import UInputTime from "@nuxt/ui/components/InputTime.vue";
import CodSheet from "./index28.js";
import { useIsMobile } from "./index107.js";
import { toBcp47 } from "./index58.js";
const _hoisted_1 = { class: "flex w-full items-center gap-1.5" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "CodDatetime",
  props: {
    modelValue: {
      type: String,
      required: false
    },
    presentation: {
      type: String,
      default: "date"
    },
    /** Calendar anchor / seed when the model is empty. */
    defaultValue: {
      type: String,
      default: void 0
    },
    /** 'YYYY-MM-DD' bounds. */
    min: {
      type: String,
      default: void 0
    },
    max: {
      type: String,
      default: void 0
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** READ-ONLY field (design §4.2 key rule: "white = the user enters, sand
     *  = the system owns the value"): sand surface, no edge, REGULAR text —
     *  readable, never faded. Distinct from disabled (dashed, faded). For
     *  calculated values, compose manually: `=` prefix (12/800 dimmed) + 700
     *  value — not a field type. */
    readonly: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { t, locale } = useI18n();
    const mobile = useIsMobile();
    const pickerOpen = ref(false);
    const dateFormat = I18n.c("general.formatDate");
    const calendarLocale = computed(() => toBcp47(locale.value));
    const inputLocale = computed(() => locale.value === "en" ? "en" : "hr");
    const parsed = computed(() => {
      if (!props.modelValue) return void 0;
      const formats = props.presentation === "time" ? ["HH:mm:ss", "HH:mm"] : void 0;
      const m = moment(props.modelValue, formats);
      return m.isValid() ? m : void 0;
    });
    const anchor = () => {
      const m = props.defaultValue ? moment(props.defaultValue) : moment();
      return m.isValid() ? m : moment();
    };
    const dateLabel = computed(() => parsed.value ? parsed.value.format(dateFormat) : dateFormat.toLowerCase());
    function parseDateSafe(value) {
      if (!value) return void 0;
      try {
        return parseDate(value);
      } catch {
        return void 0;
      }
    }
    const calendarValue = computed(() => parsed.value ? new CalendarDate(parsed.value.year(), parsed.value.month() + 1, parsed.value.date()) : void 0);
    const calendarAnchor = computed(() => {
      if (calendarValue.value || !props.defaultValue) return void 0;
      const m = anchor();
      return new CalendarDate(m.year(), m.month() + 1, m.date());
    });
    const minValue = computed(() => parseDateSafe(props.min));
    const maxValue = computed(() => parseDateSafe(props.max));
    const timeValue = computed(() => parsed.value ? new Time(parsed.value.hour(), parsed.value.minute()) : void 0);
    function commit(m) {
      const out = props.presentation === "date" ? m.format("YYYY-MM-DD") : props.presentation === "time" ? m.format("HH:mm") : m.format("YYYY-MM-DDTHH:mm:ss");
      emit("update:modelValue", out);
    }
    function onPopoverToggle(open) {
      pickerOpen.value = props.disabled ? false : open;
    }
    function onCalendarPick(value) {
      var _a;
      pickerOpen.value = false;
      if (!value || Array.isArray(value) || !("year" in value)) return;
      const base = ((_a = parsed.value) == null ? void 0 : _a.clone()) ?? anchor();
      commit(base.year(value.year).month(value.month - 1).date(value.day));
    }
    function onTimeChange(value) {
      var _a;
      const v = value;
      if (!v || typeof v.hour !== "number") return;
      const base = ((_a = parsed.value) == null ? void 0 : _a.clone()) ?? anchor();
      commit(base.hour(v.hour).minute(v.minute).second(0));
    }
    function clear() {
      emit("update:modelValue", void 0);
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1, [
        __props.presentation !== "time" ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
          !unref(mobile) ? (openBlock(), createBlock(UPopover, {
            key: 0,
            open: pickerOpen.value,
            ui: { content: "max-h-(--reka-popover-content-available-height) overflow-y-auto" },
            "onUpdate:open": onPopoverToggle
          }, {
            content: withCtx(() => [
              createVNode(UCalendar, {
                "model-value": calendarValue.value,
                "default-placeholder": calendarAnchor.value,
                "min-value": minValue.value,
                "max-value": maxValue.value,
                "week-starts-on": 1,
                locale: calendarLocale.value,
                class: "p-2",
                "onUpdate:modelValue": onCalendarPick
              }, null, 8, ["model-value", "default-placeholder", "min-value", "max-value", "locale"])
            ]),
            default: withCtx(() => [
              renderSlot(_ctx.$slots, "trigger", { label: dateLabel.value }, () => [
                createVNode(UButton, {
                  color: "neutral",
                  variant: "outline",
                  "trailing-icon": "i-lucide-calendar",
                  class: normalizeClass(["min-w-36 flex-1 justify-between rounded-input bg-default font-normal normal-case tracking-normal", [
                    parsed.value ? "text-highlighted" : "text-dimmed",
                    __props.readonly ? "bg-accented ring-transparent disabled:opacity-100 disabled:bg-accented disabled:text-highlighted" : "disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text)"
                  ]]),
                  disabled: __props.disabled || __props.readonly
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(dateLabel.value), 1)
                  ]),
                  _: 1
                }, 8, ["class", "disabled"])
              ])
            ]),
            _: 3
          }, 8, ["open"])) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
            createElementVNode("span", {
              class: "contents",
              onClick: _cache[0] || (_cache[0] = ($event) => __props.disabled || __props.readonly ? void 0 : pickerOpen.value = true)
            }, [
              renderSlot(_ctx.$slots, "trigger", { label: dateLabel.value }, () => [
                createVNode(UButton, {
                  color: "neutral",
                  variant: "outline",
                  "trailing-icon": "i-lucide-calendar",
                  class: normalizeClass(["min-w-36 flex-1 justify-between rounded-input bg-default font-normal normal-case tracking-normal", [
                    parsed.value ? "text-highlighted" : "text-dimmed",
                    __props.readonly ? "bg-accented ring-transparent disabled:opacity-100 disabled:bg-accented disabled:text-highlighted" : "disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text)"
                  ]]),
                  disabled: __props.disabled || __props.readonly
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(dateLabel.value), 1)
                  ]),
                  _: 1
                }, 8, ["class", "disabled"])
              ])
            ]),
            createVNode(CodSheet, {
              modelValue: pickerOpen.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => pickerOpen.value = $event),
              width: "360px",
              "fullscreen-mobile": false
            }, {
              default: withCtx(() => [
                createVNode(UCalendar, {
                  "model-value": calendarValue.value,
                  "default-placeholder": calendarAnchor.value,
                  "min-value": minValue.value,
                  "max-value": maxValue.value,
                  "week-starts-on": 1,
                  locale: calendarLocale.value,
                  class: "p-4",
                  "onUpdate:modelValue": onCalendarPick
                }, null, 8, ["model-value", "default-placeholder", "min-value", "max-value", "locale"])
              ]),
              _: 1
            }, 8, ["modelValue"])
          ], 64))
        ], 64)) : createCommentVNode("", true),
        __props.presentation !== "date" ? (openBlock(), createBlock(UInputTime, {
          key: 1,
          class: normalizeClass(["shrink-0", __props.readonly ? "bg-accented ring-transparent disabled:opacity-100 data-disabled:opacity-100 disabled:outline-none data-disabled:outline-none disabled:bg-accented data-disabled:bg-accented disabled:text-highlighted data-disabled:text-highlighted" : ""]),
          ui: __props.readonly ? { segment: "data-disabled:opacity-100 data-disabled:cursor-default" } : void 0,
          "model-value": timeValue.value,
          disabled: __props.disabled || __props.readonly,
          locale: inputLocale.value,
          "hour-cycle": 24,
          "onUpdate:modelValue": onTimeChange
        }, null, 8, ["class", "ui", "model-value", "disabled", "locale"])) : createCommentVNode("", true),
        __props.clearable && __props.modelValue ? (openBlock(), createBlock(UButton, {
          key: 2,
          icon: "i-lucide-x",
          color: "neutral",
          variant: "link",
          size: "sm",
          "aria-label": unref(t)("general.close"),
          disabled: __props.disabled,
          onClick: clear
        }, null, 8, ["aria-label", "disabled"])) : createCommentVNode("", true)
      ]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index30.js.map
